import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { normalizeCategory, type ReceiptCategory } from "@/lib/receiptCategory";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Globe, Building2, Menu, Upload, Receipt as ReceiptIcon, LogOut, Settings, BarChart3, User } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ReceiptDetailsContent } from "@/components/ReceiptDetailsContent";
import { formatCurrency } from "@/lib/currency";
import { UploadArea } from "@/components/UploadArea";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Footer } from "@/components/Footer";

interface Receipt {
  id: string;
  date: string;
  total: number | null;
  store_name: string;
  store_address: string | null;
  image_url: string | null;
  currency?: string | null;
}

interface SelectedReceipt {
  id: string;
  date: string;
  store: string;
  address: string | null;
  total: number;
  currency: string;
  imageUrl: string | null;
  items: Array<{
    name: string;
    amount: number;
    category: ReceiptCategory;
    quantity: number;
    price: number;
  }>;
}

const StoreDetails = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [selectedReceipt, setSelectedReceipt] = useState<SelectedReceipt | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();

  const { data: defaultCurrency } = useQuery({
    queryKey: ['profile-default-currency', user?.id],
    queryFn: async () => {
      if (!user?.id) return 'USD';

      const { data, error } = await supabase
        .from('profiles')
        .select('default_currency')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      return data?.default_currency || 'USD';
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
  });

  const handleSignOut = async () => {
    await signOut();
    toast({
      title: t('toast.success'),
      description: t('profile.accountInformation'),
    });
    navigate("/auth");
  };

  const { data: store } = useQuery({
    queryKey: ['store', storeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stores')
        .select('*')
        .eq('id', storeId)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  const { data: receipts } = useQuery({
    queryKey: ['store-receipts', storeId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('receipts')
        .select('id, date, total, store_name, store_address, image_url, currency')
        .eq('store_id', storeId)
        .eq('user_id', user.id)
        .order('date', { ascending: false });
      
      if (error) throw error;
      return data as Receipt[];
    },
  });

  const { data: lineItems } = useQuery({
    queryKey: ['receipt-items'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('line_items')
        .select('*');
      
      if (error) throw error;
      return data;
    },
  });

  const handleReceiptClick = (receipt: Receipt) => {
    const items = lineItems?.filter(item => item.receipt_id === receipt.id).map(item => ({
      name: item.product_name,
      amount: Number(item.price),
      // Same normalisation the home page applies: the DB stores free text.
      category: normalizeCategory(item.category),
      quantity: Number(item.quantity),
      price: Number(item.price)
    })) || [];

    setSelectedReceipt({
      id: receipt.id,
      date: receipt.date,
      store: receipt.store_name,
      address: receipt.store_address,
      total: Number(receipt.total || 0),
      currency: receipt.currency || storeCurrency,
      imageUrl: receipt.image_url,
      items
    });
  };

  if (!store) {
    return <div className="p-4">{t('common.loading')}</div>;
  }

  // Calculate total spend at this store
  const currencyFallback = defaultCurrency || 'USD';
  const storeCurrency = receipts?.find((receipt) => receipt.currency)?.currency || currencyFallback;
  const totalSpend = receipts?.reduce((sum, receipt) => sum + Number(receipt.total || 0), 0) || 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b shadow-custom-sm">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72">
                  <SheetHeader>
                    <SheetTitle className="text-left">{t('common.menu')}</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 flex flex-col gap-2">
                    <div
                      className="mb-4 rounded-lg bg-accent p-4 cursor-pointer hover:bg-accent/80 transition-colors"
                      onClick={() => navigate('/profile')}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary">
                          <User className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user?.email || "User"}</p>
                          <p className="text-xs text-muted-foreground">{t('navigation.profile')}</p>
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => navigate('/?tab=receipts')}
                    >
                      <ReceiptIcon className="mr-3 h-4 w-4" />
                      {t('navigation.receipts')}
                    </Button>

                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => navigate('/stores')}
                    >
                      <Building2 className="mr-3 h-4 w-4" />
                      {t('navigation.stores')}
                    </Button>

                    <Button variant="ghost" className="justify-start" disabled>
                      <BarChart3 className="mr-3 h-4 w-4" />
                      {t('navigation.analytics')}
                      <span className="ml-auto text-xs text-muted-foreground">{t('common.comingSoon')}</span>
                    </Button>

                    <Button
                      variant="ghost"
                      className="justify-start"
                      onClick={() => navigate('/profile')}
                    >
                      <Settings className="mr-3 h-4 w-4" />
                      {t('navigation.settings')}
                    </Button>

                    <Button
                      variant="ghost"
                      className="justify-start text-destructive hover:text-destructive"
                      onClick={handleSignOut}
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      {t('common.signOut')}
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
              
              <div>
                <h1
                  className="text-3xl font-bold bg-clip-text text-transparent cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => navigate('/')}
                >
                  {t('common.appName')}
                </h1>
                <p className="text-muted-foreground mt-1">
                  {t('common.appDescription')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                size="lg"
                className="transition-all duration-300"
                onClick={() => setShowUpload(true)}
              >
                <Upload className="mr-2 h-4 w-4" />
                {t('buttons.uploadReceipt')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-6">
        <h2 className="text-2xl font-bold">{store.name}</h2>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t('stores.storeInformation')}</span>
            <div className="text-right">
              <p className="text-sm text-muted-foreground font-normal">{t('stores.totalSpend')}</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(totalSpend, storeCurrency)}</p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {store.address && (
            <div className="flex items-start gap-2">
              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">{t('stores.address')}</p>
                <p className="text-sm text-muted-foreground">{store.address}</p>
                {store.city && <p className="text-sm text-muted-foreground">{store.city}</p>}
                {store.country && <p className="text-sm text-muted-foreground">{store.country}</p>}
              </div>
            </div>
          )}
          
          {store.website && (
            <div className="flex items-start gap-2">
              <Globe className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="font-medium">{t('stores.website')}</p>
                <a href={store.website} target="_blank" rel="noopener noreferrer"
                   className="text-sm text-primary hover:underline">
                  {store.website}
                </a>
              </div>
            </div>
          )}

          {(store.siret || store.siren || store.eu_tva_number) && (
            <div className="flex items-start gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium">{t('stores.businessInformation')}</p>
                {store.siret && <p className="text-sm text-muted-foreground">{t('stores.siret')}: {store.siret}</p>}
                {store.siren && <p className="text-sm text-muted-foreground">{t('stores.siren')}: {store.siren}</p>}
                {store.eu_tva_number && <p className="text-sm text-muted-foreground">{t('stores.euTva')}: {store.eu_tva_number}</p>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div>
        <h2 className="text-xl font-semibold mb-4">
          {t('stores.yourReceipts', { count: receipts?.length || 0 })}
        </h2>
        <div className="space-y-3">
          {receipts?.map((receipt) => (
            <Card 
              key={receipt.id} 
              className="cursor-pointer hover:bg-accent transition-colors"
              onClick={() => handleReceiptClick(receipt)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{format(new Date(receipt.date), 'MMM dd, yyyy')}</p>
                    <p className="text-sm text-muted-foreground">{receipt.store_name}</p>
                  </div>
                  <Badge variant="secondary" className="text-lg font-semibold">
                    {formatCurrency(Number(receipt.total), receipt.currency || storeCurrency)}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={!!selectedReceipt} onOpenChange={() => setSelectedReceipt(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {selectedReceipt && <ReceiptDetailsContent receipt={selectedReceipt} />}
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <UploadArea
            onUpload={(file) => {
              console.log('File uploaded:', file);
              setShowUpload(false);
              window.location.reload();
            }}
          />
        </DialogContent>
      </Dialog>
      </div>
      <Footer />
    </div>
  );
};

export default StoreDetails;